import React, { useState, useRef } from 'react';
import { 
  CloudArrowUpIcon, 
  PhotoIcon, 
  XMarkIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

const ImageUploader = ({ 
  onImagesChange, 
  maxImages = 5, 
  maxFileSize = 10 * 1024 * 1024, // 10MB
  acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  className = ''
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [images, setImages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Dosya işleme fonksiyonu - Orijinal dosyayı koru (hiç işleme yapma)
  const processImage = async (file) => {
    // Orijinal dosyayı olduğu gibi döndür
    return file;
  };

  // Data URL oluştur
  const createDataURL = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Data URL creation failed'));
      reader.readAsDataURL(blob);
    });
  };

  // Dosya validasyonu
  const validateFile = (file) => {
    // Dosya tipi kontrolü
    if (!acceptedTypes.includes(file.type)) {
      throw new Error(`${file.name} desteklenmeyen dosya formatı. Desteklenen formatlar: JPEG, PNG, GIF, WebP`);
    }
    
    // Dosya boyutu kontrolü
    if (file.size > maxFileSize) {
      const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
      throw new Error(`${file.name} dosyası çok büyük. Maksimum ${maxSizeMB}MB olmalıdır.`);
    }
    
    return true;
  };

  // Dosya ekleme fonksiyonu
  const addFiles = async (files) => {
    if (images.length + files.length > maxImages) {
      setError(`Maksimum ${maxImages} görsel yükleyebilirsiniz.`);
      return;
    }

    setIsProcessing(true);
    setError(null);
    
    try {
      const newImages = [];
      
      for (const file of files) {
        // Dosya validasyonu
        validateFile(file);
        
         // Orijinal dosyayı koru
         const originalFile = file;
         
         // Dosya adını düzelt
         const fileName = file.name || `image_${Date.now()}.jpg`;
         
         // Yeni görsel objesi oluştur
         const imageObj = {
           id: Math.random().toString(36).substr(2, 9),
           file: originalFile,
           originalFile: file,
           name: fileName,
           size: file.size,
           type: file.type,
           preview: await createDataURL(originalFile),
           description: '',
           tags: [],
           uploadedAt: new Date().toISOString()
         };
        
        newImages.push(imageObj);
      }
      
      // State'i güncelle
      const updatedImages = [...images, ...newImages];
      setImages(updatedImages);
      onImagesChange(updatedImages);
      
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Dosya seçme handler'ı
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      addFiles(files);
    }
    // Input'u temizle
    e.target.value = '';
  };

  // Drag & Drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      addFiles(files);
    }
  };

  // Görsel silme fonksiyonu
  const removeImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    setImages(updatedImages);
    onImagesChange(updatedImages);
  };

  // Dosya boyutu formatı
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
          dragActive 
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        } ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center space-y-3">
          <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full">
            <PhotoIcon className="w-8 h-8 text-gray-400" />
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Görselleri buraya sürükleyin veya
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              disabled={isProcessing}
            >
              dosya seçin
            </button>
          </div>
          
          <p className="text-xs text-gray-500 dark:text-gray-400">
            PNG, JPG, GIF, WebP (Maksimum {Math.round(maxFileSize / (1024 * 1024))}MB)
          </p>
          
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {images.length}/{maxImages} görsel yüklendi
          </p>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          disabled={isProcessing}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Processing Indicator */}
      {isProcessing && (
        <div className="flex items-center justify-center space-x-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <p className="text-sm text-blue-700 dark:text-blue-400">Görseller işleniyor...</p>
        </div>
      )}

      {/* Uploaded Images Preview */}
      {images.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Yüklenen Görseller ({images.length})
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {images.map((image) => (
              <div key={image.id} className="relative group">
                <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                  <img
                    src={image.preview}
                    alt={image.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                  <button
                    onClick={() => removeImage(image.id)}
                    className="opacity-0 group-hover:opacity-100 p-2 bg-red-500 rounded-full text-white hover:bg-red-600 transition-all duration-200"
                    title="Görseli sil"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Image Info */}
                <div className="mt-1">
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate" title={image.name}>
                    {image.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {formatFileSize(image.size)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
